import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ClipboardList, Pencil } from "lucide-react";
import { ORDER_STATUS_DEFINITIONS, updateCustomerSchema, type OrderStatus } from "@laundrypro/shared";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorText,
  Input,
  Modal,
  PageHeader,
  Skeleton,
  StatTile,
  Table,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { ApiError, apiFetch } from "../lib/api";
import { currencyLabel, dateTime, money } from "../lib/format";

interface CustomerOrder {
  id: string;
  orderRef: string;
  status: OrderStatus;
  totalMinor: number;
  balanceDueMinor: number;
  receivedAt: string;
  createdAt: string;
}

interface CustomerDetail {
  id: string;
  name: string;
  mobile: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  orderCount: number;
  outstandingMinor: number;
  totalSpentMinor: number;
  createdAt: string;
  orders: CustomerOrder[];
}

/**
 * Customer detail: who they are, what they owe, and their orders.
 *
 * Operational and dense on purpose — the figures answer "can I hand over the
 * clothes / do they owe us anything", not "how is this cohort trending".
 */
export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const { language } = useBranding();
  const { showToast } = useToast();
  const ar = language === "ar";
  const currency = currencyLabel(language);

  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", mobile: "", email: "", address: "", notes: "" });
  const [editError, setEditError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoadError(null);
    try {
      const data = await apiFetch<CustomerDetail>(`/customers/${id}`);
      setCustomer(data);
      setEditForm({
        name: data.name,
        mobile: data.mobile ?? "",
        email: data.email ?? "",
        address: data.address ?? "",
        notes: data.notes ?? "",
      });
    } catch (err) {
      setLoadError(
        err instanceof ApiError && err.status === 404
          ? ar
            ? "العميل غير موجود."
            : "This customer does not exist."
          : err instanceof ApiError
            ? err.message
            : ar
              ? "تعذّر تحميل العميل."
              : "Couldn't load this customer.",
      );
    }
  }, [id, ar]);

  useEffect(() => {
    void load();
  }, [load]);

  const saveEdit = async (event: FormEvent) => {
    event.preventDefault();
    setEditError(null);
    setFieldErrors({});

    const parsed = updateCustomerSchema.safeParse({
      name: editForm.name,
      mobile: editForm.mobile || null,
      email: editForm.email || null,
      address: editForm.address || null,
      notes: editForm.notes || null,
    });
    if (!parsed.success) {
      const errors: Record<string, string> = {};
      for (const issue of parsed.error.issues) errors[issue.path.join(".")] ??= issue.message;
      setFieldErrors(errors);
      return;
    }

    setSaving(true);
    try {
      await apiFetch(`/customers/${id}`, { method: "PATCH", body: parsed.data });
      showToast("success", ar ? "تم حفظ التعديلات." : "Changes saved.");
      setEditOpen(false);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        setEditError(
          err.code === "mobile_already_registered"
            ? ar
              ? "رقم الجوال مسجّل لعميل آخر."
              : "That mobile number belongs to another customer."
            : err.message,
        );
        if (err.fields) setFieldErrors(err.fields);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-4">
        <PageHeader title={ar ? "العملاء" : "Customers"} />
        <Card>
          <EmptyState
            icon={<AlertTriangle className="size-6" />}
            title={ar ? "تعذّر تحميل العميل" : "Couldn't load this customer"}
            description={loadError}
            action={
              <Button size="sm" onClick={load}>
                {ar ? "إعادة المحاولة" : "Retry"}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-7 w-48" />
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
          <Skeleton className="h-24 w-full rounded-xl" />
        </div>
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={customer.name}
        subtitle={customer.mobile ?? undefined}
        trailing={
          hasPermission("customers.update") ? (
            <Button
              variant="outline"
              size="sm"
              icon={<Pencil className="size-3.5" />}
              onClick={() => setEditOpen(true)}
            >
              {ar ? "تعديل" : "Edit"}
            </Button>
          ) : undefined
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatTile label={ar ? "عدد الطلبات" : "Orders"} value={String(customer.orderCount)} />
        <StatTile
          label={ar ? "الرصيد المستحق" : "Outstanding balance"}
          value={`${money(customer.outstandingMinor)} ${currency}`}
          tone={customer.outstandingMinor > 0 ? "accent" : "default"}
        />
        <StatTile
          label={ar ? "إجمالي التعامل" : "Total spent"}
          value={`${money(customer.totalSpentMinor)} ${currency}`}
        />
      </div>

      {(customer.email || customer.address || customer.notes) && (
        <Card>
          <CardHeader title={ar ? "بيانات العميل" : "Customer information"} />
          <div className="space-y-1.5 text-sm">
            {customer.email && (
              <div className="flex justify-between gap-3 border-b border-line-hairline py-2 last:border-0">
                <span className="text-muted-foreground">{ar ? "البريد" : "Email"}</span>
                <span className="text-ink-900">{customer.email}</span>
              </div>
            )}
            {customer.address && (
              <div className="flex justify-between gap-3 border-b border-line-hairline py-2 last:border-0">
                <span className="text-muted-foreground">{ar ? "العنوان" : "Address"}</span>
                <span className="text-ink-900">{customer.address}</span>
              </div>
            )}
            {customer.notes && (
              <div className="flex justify-between gap-3 border-b border-line-hairline py-2 last:border-0">
                <span className="text-muted-foreground">{ar ? "ملاحظات" : "Notes"}</span>
                <span className="text-ink-900">{customer.notes}</span>
              </div>
            )}
          </div>
        </Card>
      )}

      <Card padding="none">
        <div className="p-5 pb-0 sm:p-6 sm:pb-0">
          <CardHeader
            title={ar ? "الطلبات" : "Orders"}
            description={ar ? "أحدث ٥٠ طلباً" : "The most recent 50 orders"}
          />
        </div>
        {customer.orders.length === 0 ? (
          <EmptyState
            icon={<ClipboardList className="size-6" />}
            title={ar ? "لا توجد طلبات بعد" : "No orders yet"}
            description={
              ar ? "ستظهر طلبات هذا العميل هنا فور استلامها." : "This customer's orders appear here once taken in."
            }
          />
        ) : (
          <div className="px-5 pb-5 sm:px-6 sm:pb-6">
            <Table>
              <THead>
                <tr>
                  <Th>{ar ? "رقم الطلب" : "Order"}</Th>
                  <Th>{ar ? "الحالة" : "Status"}</Th>
                  <Th>{ar ? "الإجمالي" : "Total"}</Th>
                  <Th>{ar ? "المستحق" : "Balance"}</Th>
                  <Th>{ar ? "التاريخ" : "Date"}</Th>
                </tr>
              </THead>
              <tbody>
                {customer.orders.map((order) => {
                  const def = ORDER_STATUS_DEFINITIONS[order.status];
                  return (
                    <Tr key={order.id}>
                      <Td className="tabular-nums font-medium text-ink-900">
                        <Link to={`/orders/${order.id}`} className="hover:text-accent-600 hover:underline">
                          {order.orderRef}
                        </Link>
                      </Td>
                      <Td>
                        <Badge tone={def.tone}>{ar ? def.labelAr : def.labelEn}</Badge>
                      </Td>
                      <Td className="tabular-nums font-medium">
                        {money(order.totalMinor)} {currency}
                      </Td>
                      <Td className="tabular-nums">
                        {order.balanceDueMinor > 0 ? (
                          <span className="font-medium text-warning">
                            {money(order.balanceDueMinor)} {currency}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">0.00 {currency}</span>
                        )}
                      </Td>
                      <Td className="tabular-nums text-muted-foreground">{dateTime(order.createdAt, language)}</Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={ar ? "تعديل العميل" : "Edit customer"}
        maxWidth="max-w-md"
        closeLabel={ar ? "إغلاق" : "Close"}
      >
        <form onSubmit={saveEdit} className="space-y-3">
          <Input
            label={ar ? "الاسم" : "Name"}
            value={editForm.name}
            onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            error={fieldErrors.name}
            required
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={ar ? "الجوال" : "Mobile"}
              value={editForm.mobile}
              onChange={(e) => setEditForm((f) => ({ ...f, mobile: e.target.value }))}
              error={fieldErrors.mobile}
              inputMode="tel"
            />
            <Input
              label={ar ? "البريد" : "Email"}
              type="email"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
              error={fieldErrors.email}
            />
          </div>
          <Input
            label={ar ? "العنوان" : "Address"}
            value={editForm.address}
            onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
            error={fieldErrors.address}
          />
          <Input
            label={ar ? "ملاحظات" : "Notes"}
            value={editForm.notes}
            onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
            error={fieldErrors.notes}
          />

          <ErrorText>{editError}</ErrorText>

          <div className="mt-4 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setEditOpen(false)}>
              {ar ? "إلغاء" : "Cancel"}
            </Button>
            <Button type="submit" loading={saving}>
              {ar ? "حفظ" : "Save"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
